import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SiteProvider } from "@/contexts/SiteContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";

import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Products from "@/pages/public/Products";
import ProductDetail from "@/pages/public/ProductDetail";
import Brands from "@/pages/public/Brands";
import Categories from "@/pages/public/Categories";
import Gallery from "@/pages/public/Gallery";
import Blogs from "@/pages/public/Blogs";
import BlogDetail from "@/pages/public/BlogDetail";
import FAQPage from "@/pages/public/FAQ";
import Contact from "@/pages/public/Contact";
import RequestCatalogue from "@/pages/public/RequestCatalogue";
import RequestQuotation from "@/pages/public/RequestQuotation";
import NotFound from "@/pages/public/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminBlogs from "@/pages/admin/AdminBlogs";
import AdminFAQ from "@/pages/admin/AdminFAQ";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminChangeCredentials from "@/pages/admin/AdminChangeCredentials";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-gold text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <SiteProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-right" richColors />
            <Routes>
              {/* Public */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:slug" element={<ProductDetail />} />
                <Route path="/brands" element={<Brands />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:slug" element={<BlogDetail />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/request-catalogue" element={<RequestCatalogue />} />
                <Route path="/request-quotation" element={<RequestQuotation />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="brands" element={<AdminBrands />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="blogs" element={<AdminBlogs />} />
                <Route path="faqs" element={<AdminFAQ />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="account" element={<AdminChangeCredentials />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </SiteProvider>
    </ThemeProvider>
  );
}

export default App;
